import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDown,
  ArrowUpRight,
  Cpu,
  LineChart,
  BatteryCharging,
  Building,
  Building2,
} from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import CountUp from "../components/CountUp";
import { useLanguage } from "../context/LanguageContext";
import { sized } from "../lib/images";

const HERO_IMG =
  "https://images.pexels.com/photos/16586150/pexels-photo-16586150.jpeg";

// DICIONÁRIO DE TRADUÇÕES (Organizado para alternar dinamicamente)
const TRANSLATIONS = {
  en: {
    heroEyebrow: "Engineering · Automation · Renewables",
    heroDesc: "Integrated engineering company delivering control, automation and software solutions for energy systems and technical infrastructure. Since 2008, from the University of Coimbra to the world.",
    btnSolutions: "View solutions",
    btnTalkEng: "Talk to engineering",
    scrollExplore: "Scroll to explore",
    marquee: ["POWER PLANT CONTROLLER", "SCADA", "BESS", "JANUS", "BMS", "SOLAR AUTOMATION"],
    aboutEyebrow: "Who we are",
    aboutTitle: "The control layer behind utility-scale renewables.",
    aboutDesc: "Founded in 2008 as a spin-off of the Department of Mechanical Engineering of the University of Coimbra, WSBP combines its in-house monitoring platform — JANUS — with advanced control solutions (PPC, SCADA and BESS) for the renewable energy sector.",
    statMw: "Integrated capacity",
    statPlants: "Plants in operation",
    statMarkets: "Active markets",
    statMarketsVal: "All EU",
    productsEyebrow: "Products",
    productsTitle: "Five products. One engineering platform.",
    btnSeeAll: "See all",
    learnMore: "Learn more",
    productsList: [
      { tag: "PPC", hash: "ppc", title: "Power Plant Controller", desc: "Real-time coordination, complying with grid operator setpoints." },
      { tag: "SCADA", hash: "scada", title: "SCADA Systems", desc: "Custom supervision, real-time KPIs and alarms." },
      { tag: "BESS", hash: "bess", title: "Energy Storage (BESS)", desc: "SoC management, power flow and electricity market interaction." },
      { tag: "JANUS", hash: "janus", title: "JANUS", desc: "Web monitoring platform for energy efficiency and sustainability." },
      { tag: "BMS", hash: "bms", title: "Building Management System", desc: "Centralised technical management of HVAC, lighting, energy and alarms in buildings." }
    ],
    capabilitiesEyebrow: "Capabilities",
    capabilitiesTitle: "From substation to control room — fully integrated, end-to-end solutions.",
    featuresList: [
      { icon: Cpu, title: "Power Plant Controller", desc: "Real-time coordination of active and reactive power, voltage and frequency, complying with grid operator setpoints." },
      { icon: LineChart, title: "Custom SCADA", desc: "Full supervision of inverters, trackers, weather stations and more, with KPIs and alarm management." },
      { icon: BatteryCharging, title: "BESS & hybrids", desc: "SoC management, power flow control and safe interaction with the electricity market." },
      { icon: Building, title: "Substations", desc: "Automation and SCADA for electrical substations." },
      { icon: Building2, title: "Building Management", desc: "Centralised technical management (BMS) of HVAC, lighting, energy and alarms in buildings." }
    ],
    ctaEyebrow: "Next step",
    ctaTitle: "Ready to automate?",
    ctaDesc: "We assess your project and design the ideal control architecture — PV, BESS, hybrid, substation or building.",
    btnTalkTeam: "Talk to the team",
    btnSeeProjects: "See projects"
  },
  pt: {
    heroEyebrow: "Engenharia · Automação · Renováveis",
    heroDesc: "Empresa de engenharia integrada que desenvolve soluções de controlo, automação e software para sistemas de energia e infraestruturas técnicas. Desde 2008, da Universidade de Coimbra para o mundo.",
    btnSolutions: "Ver soluções",
    btnTalkEng: "Falar com a engenharia",
    scrollExplore: "Desça para explorar",
    marquee: ["POWER PLANT CONTROLLER", "SCADA", "BESS", "JANUS", "BMS", "AUTOMAÇÃO SOLAR"],
    aboutEyebrow: "Quem Somos",
    aboutTitle: "A camada de controlo por trás das energias renováveis em grande escala.",
    aboutDesc: "Fundada em 2008 como uma spin-off do Departamento de Engenharia Mecânica da Universidade de Coimbra, a WSBP combina a sua própria plataforma de monitorização — JANUS — com soluções avançadas de controlo (PPC, SCADA e BESS) para o setor das energias renováveis.",
    statMw: "Capacidade integrada",
    statPlants: "Centrais em operação",
    statMarkets: "Mercados ativos",
    statMarketsVal: "Toda a UE",
    productsEyebrow: "Produtos",
    productsTitle: "Cinco produtos. Uma plataforma de engenharia.",
    btnSeeAll: "Ver todos",
    learnMore: "Saber mais",
    productsList: [
      { tag: "PPC", hash: "ppc", title: "Power Plant Controller", desc: "Coordenação em tempo real, cumprindo as exigências e setpoints do operador de rede." },
      { tag: "SCADA", hash: "scada", title: "Sistemas SCADA", desc: "Supervisão personalizada, com KPIs e gestão de alarmes em tempo real." },
      { tag: "BESS", hash: "bess", title: "Armazenamento (BESS)", desc: "Gestão do estado de carga (SoC), fluxos de energia e integração com o mercado elétrico." },
      { tag: "JANUS", hash: "janus", title: "JANUS", desc: "Plataforma web de monitorização para eficiência energética e sustentabilidade." },
      { tag: "BMS", hash: "bms", title: "Gestão Técnica Centralizada", desc: "Gestão técnica centralizada de AVAC, iluminação, energia e alarmes em edifícios." }
    ],
    capabilitiesEyebrow: "Capacidades",
    capabilitiesTitle: "Da subestação à sala de controlo — soluções integradas de ponta a ponta.",
    featuresList: [
      { icon: Cpu, title: "Power Plant Controller", desc: "Coordenação em tempo real de potência ativa e reativa, tensão e frequência, cumprindo os setpoints do operador de rede." },
      { icon: LineChart, title: "SCADA Personalizado", desc: "Supervisão total de inversores, trackers, estações meteorológicas e muito mais, com KPIs e gestão de alarmes." },
      { icon: BatteryCharging, title: "BESS e Híbridos", desc: "Gestão de SoC, controlo do fluxo de potência e interação segura com o mercado elétrico." },
      { icon: Building, title: "Subestações", desc: "Automação e sistemas SCADA para subestações elétricas." },
      { icon: Building2, title: "Gestão Técnica (BMS)", desc: "Gestão técnica centralizada de AVAC, iluminação, energia e alarmes em edifícios." }
    ],
    ctaEyebrow: "Próximo passo",
    ctaTitle: "Pronto para automatizar?",
    ctaDesc: "Avaliamos o seu projeto e desenhamos a arquitetura de controlo ideal — seja fotovoltaica, BESS, híbrida, subestação ou edifício.",
    btnTalkTeam: "Falar com a equipa",
    btnSeeProjects: "Ver projetos"
  }
};

export default function Home() {
  const heroRef = useRef(null);
  const { language } = useLanguage();
  const t = TRANSLATIONS[language];

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroTitleY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const heroOverlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  // Imagens estáticas dos produtos para o mapeamento
  const productImages = {
    PPC: "https://images.pexels.com/photos/5203849/pexels-photo-5203849.jpeg",
    SCADA: "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/skyadi8j_SCADA.png",
    BESS: "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/fhc0bbvj_BESS.png",
    JANUS: "https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/enouycxf_image.png",
    BMS: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg"
  };

  return (
    <div className="relative" data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[60vh] overflow-hidden" 
        data-testid="home-hero"
      >
        <motion.div style={{ y: heroImgY }} className="absolute inset-0 -z-[1]">
          <div className="absolute inset-0 scale-110">
            <img
              src={sized(HERO_IMG, 1920)}
              alt="Solar plant background"
              className="h-full w-full object-cover"
              decoding="async"
            />
          </div>
        </motion.div>

        <motion.div
          style={{ opacity: heroOverlayOpacity }}
          className="absolute inset-0 -z-[1] bg-black"
        />
        <div className="absolute inset-0 -z-[1] bg-gradient-to-b from-black/50 via-black/30 to-[#0A0A0A]" />

        <div className="mx-auto flex min-h-[60vh] max-w-[1400px] flex-col justify-start px-6 pb-20 pt-40 md:px-12 lg:px-16">
          <motion.div style={{ y: heroTitleY }} className="max-w-5xl">
            <Reveal>
              <div className="eyebrow -mt-6 md:-mt-8 mb-6 md:mb-8">
                {t.heroEyebrow}
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <h1 className="font-display mt-2 font-black uppercase leading-[0.92] tracking-tighter text-white text-6xl sm:text-6xl lg:text-[6rem]">
                <span className="text-[#F68A31] text-glow">WSBP</span> 
                <span className="text-4xl sm:text-4xl lg:text-[4rem] ml-2 normal-case font-medium">Electronics</span>
              </h1>
            </Reveal>

            <Reveal delay={0.15}>
              <p className="mt-8 max-w-2xl text-lg text-white/75 md:text-xl">
                {t.heroDesc}
              </p>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/products" className="btn-primary" data-testid="hero-cta-products">
                  {t.btnSolutions} <ArrowUpRight size={18} strokeWidth={2.5} />
                </Link>
                <Link to="/contact" className="btn-secondary" data-testid="hero-cta-contact">
                  {t.btnTalkEng} <ArrowUpRight size={18} />
                </Link>
              </div>
            </Reveal>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-20 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/55"
          >
            <ArrowDown size={14} className="animate-bounce text-[#F68A31]" />
            {t.scrollExplore}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="relative z-10 overflow-hidden border-y border-white/10 bg-black/50 backdrop-blur-sm py-6">
        {/* Adicione 'animate-marquee' aqui na className */}
        <div className="marquee-track flex w-max font-display text-3xl font-black uppercase tracking-tight text-white/15 md:text-5xl animate-marquee">
          {Array.from({ length: 4 }).map((_, copy) => (
            <div
              key={copy}
              aria-hidden={copy === 1}
              className="flex shrink-0 items-center gap-16 pr-16"
            >
              {t.marquee.flatMap((label, idx) => [
                <span key={`l-${idx}`}>{label}</span>,
                <span key={`d-${idx}`} className="text-[#F68A31]"> • </span>,
              ])}
            </div>
          ))}
        </div>
      </section>

      {/* WHO WE ARE */}
      <section
        className="relative z-10 mx-auto max-w-[1400px] px-6 py-32 md:px-12 lg:px-16"
        data-testid="home-about"
      >
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="eyebrow">{t.aboutEyebrow}</div>

              <h2 className="font-display mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
                {t.aboutTitle}
              </h2>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:pt-4">
            <Reveal delay={0.1}>
              <p className="text-lg text-white/75">
                {t.aboutDesc}
              </p>
            </Reveal>

            {/* CARD DE STATS COM ATUALIZAÇÃO REATIVA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05, margin: "0px 0px -100px 0px" }}
              transition={{ duration: 0.5 }}
              className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 sm:grid-cols-3"
            >
              {/* CARD 1 */}
              <div className="bg-[#0A0A0A] p-6">
                <div className="font-display text-xl font-black text-[#F68A31] whitespace-nowrap md:text-2xl">
                  <CountUp value={1500} suffix=" MW+" duration={2} />
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-white/55">
                  {t.statMw}
                </div>
              </div>

              {/* CARD 2 */}
              <div className="bg-[#0A0A0A] p-6">
                <div className="font-display text-xl font-black text-[#F68A31] whitespace-nowrap md:text-2xl">
                  <CountUp value={300} duration={2} />
                  <span>+</span>
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-white/55">
                  {t.statPlants}
                </div>
              </div>

              {/* CARD 3 */}
              <div className="bg-[#0A0A0A] p-6">
                <div className="font-display text-xl font-black text-[#F68A31] whitespace-nowrap md:text-2xl">
                  {t.statMarketsVal}
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-white/55">
                  {t.statMarkets
                }
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section className="relative z-10 mx-auto max-w-[1400px] px-6 py-24 md:px-12 lg:px-16" data-testid="home-products">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Reveal>
              <div className="eyebrow">{t.productsEyebrow}</div>
              <h2 className="font-display mt-6 max-w-6xl text-4xl font-bold tracking-tight sm:text-5xl">
                {t.productsTitle}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <Link to="/products" className="btn-secondary text-sm whitespace-nowrap">
              {t.btnSeeAll} <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {t.productsList.map((p, i) => (
            <Reveal key={p.tag} delay={i * 0.08}>
              <Link
                to={`/products#${p.hash}`}
                data-testid={`product-card-${p.tag.toLowerCase().replace(/[^a-z]/g, "")}`}
                className="group relative block h-[440px] overflow-hidden rounded-3xl border border-white/10 bg-black"
              >
                <img
                  src={sized(productImages[p.tag], 800)}
                  alt={p.title}
                  className="absolute inset-0 h-full w-full object-cover opacity-50 transition-transform duration-700 group-hover:scale-105 group-hover:opacity-60"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
                <div className="relative z-10 flex h-full flex-col justify-between p-7">
                  <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#F68A31]/40 bg-[#F68A31]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#F68A31]">
                    {p.tag}
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-bold sm:text-3xl">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-white/75">{p.desc}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#F68A31]">
                      {t.learnMore}{" "}
                      <ArrowUpRight
                        size={16}
                        className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </div>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CAPACIDADES / FEATURES */}
      <section className="relative z-10 mx-auto max-w-[1400px] px-6 py-24 md:px-12 lg:px-16">
        <Reveal>
          <div className="eyebrow">{t.capabilitiesEyebrow}</div>
          <h2 className="font-display mt-6 max-w-5xl text-4xl font-bold tracking-tight sm:text-5xl">
            {t.capabilitiesTitle}
          </h2>
        </Reveal>
        <StaggerGroup className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {/* Keyed by position, not by title: the titles are translated, so a
              language switch would give every card a new key, remount it into
              StaggerGroup's "hidden" variant, and leave it stuck at opacity 0 —
              the group's whileInView has viewport={{ once: true }} and never
              fires again. The list is a fixed five entries in the same order in
              both languages. */}
          {t.featuresList.map((f, i) => {
            // The icon travels with the entry (see featuresList), so there is no
            // lookup by title to fall out of sync with the copy.
            const IconComponent = f.icon;

            return (
              <StaggerItem
                key={i}
                className="group rounded-3xl border border-white/10 bg-white/[0.03] p-7 transition-colors hover:border-[#F68A31]/50"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F68A31]/10 text-[#F68A31] transition-colors group-hover:bg-[#F68A31] group-hover:text-black">
                  <IconComponent size={22} />
                </div>
                <h3 className="font-display mt-6 text-xl font-semibold">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/65">
                  {f.desc}
                </p>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </section>

      {/* CTA */}
      <section className="relative z-10 px-6 pb-32 pt-12 md:px-12 lg:px-16">
        <div className="mx-auto max-w-[1400px] overflow-hidden rounded-[36px] border border-[#F68A31]/30 bg-gradient-to-br from-[#F68A31]/15 via-black to-black p-10 md:p-16">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="eyebrow">{t.ctaEyebrow}</div>
                <h2 className="font-display mt-6 text-4xl font-bold tracking-tight sm:text-6xl">
                  {t.ctaTitle}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-xl text-lg text-white/75">
                  {t.ctaDesc}
                </p>
              </Reveal>
            </div>
            <div className="flex flex-wrap gap-4 lg:col-span-5 lg:justify-end">
              <Link to="/contact" className="btn-primary" data-testid="cta-contact">
                {t.btnTalkTeam} <ArrowUpRight size={18} />
              </Link>
              <Link to="/projects" className="btn-secondary" data-testid="cta-projects">
                {t.btnSeeProjects} <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}